/**
 * Renames the "Downloads" experience to "Exports". Keep every PostHog release condition at
 * 100%: the error email buckets on the requester's email (it has no user row) while the
 * client buckets on user ID, so a partial rollout can disagree between the two.
 */
import { useFeatureFlagEnabled } from '@posthog/react';

export const EXPORT_TERMINOLOGY_FLAG = 'export-terminology-enabled';

export function useExportTerminologyEnabled(): boolean {
  return useFeatureFlagEnabled(EXPORT_TERMINOLOGY_FLAG) === true;
}

export type ExportLabels = ReturnType<typeof getExportLabels>;

/** Resolved in one place so a screen can't end up half-renamed. */
export function getExportLabels(enabled: boolean) {
  return {
    // Nav + tab headers
    navItem: enabled ? 'Exports' : 'Downloads',
    historyTab: enabled ? 'Export History' : 'Download History',
    newTab: enabled ? 'New Export' : 'New Download',

    // FME extract form
    historyButton: enabled ? 'Export History' : 'Download History',
    formIntro: enabled
      ? 'Your selected layers will be exported for the entire project boundary in the chosen CAD/GIS format and projection.'
      : 'Your selected layers will be downloaded for the entire project boundary in the chosen CAD/GIS format and projection.',
    areaToBeExported: enabled ? 'Area to be Exported:' : 'Area to be Downloaded:',
    layerPickerLabel: enabled ? 'Select Layers to Export' : 'Select Layers to Download',
    submitButton: enabled ? 'Export Selected Data' : 'Download Selected Data',
    confirmModalTitle: enabled ? 'Confirm Export' : 'Confirm Download',
    // The export is emailed either way, so the Downloads/Exports rename has nothing to rename here.
    confirmButton: 'Acknowledge & Send Email',
    confirmCancelButton: 'Cancel',
    confidentialityNote: enabled
      ? 'By exporting the requested information, I acknowledge and agree that the requested information represents'
      : 'By downloading the requested information, I acknowledge and agree that the requested information represents',
    confirmModalBody: enabled
      ? 'The exported data is confidential, and can only be shared with internal and external project team members who need this data for design and construction purposes. The drawings containing the data should be marked as confidential.'
      : 'The downloaded data is confidential, and can only be shared with internal and external project team members who need this data for design and construction purposes. The drawings containing the data should be marked as confidential.',
    submitToast: enabled
      ? 'We are compiling your export now! Depending on the size, this could take some time. You will receive an email notification when it is ready. You can also monitor its status right here.'
      : 'We are compiling your download now! Depending on the size, this could take some time. You will receive an email notification when it is ready. You can also monitor its status right here.',
    submitErrorToast: enabled
      ? 'There was an error during the export process. Please contact support.'
      : 'There was an error during the download process. Please contact support.',
    // The flag-off string keeps its existing (ungrammatical) wording so turning the flag off
    // still reproduces production copy exactly.
    notEntitled: enabled
      ? '⚠️ Exports are not included for your project.'
      : '⚠️ Downloads is not included for your project.',
    noPermission: enabled
      ? "You don't have permission to export data. Ask your org admin."
      : "You don't have permission to download data. Ask your org admin.",
    notEntitledUpgrade: enabled
      ? 'your project to enable CAD & GIS exports.'
      : 'your project to enable CAD & GIS downloads.',

    // Skipped layers
    droppedLayersNotice: (layerNames: string) =>
      enabled
        ? `Skipped ${layerNames} — no data inside your project boundary.`
        : `Skipped ${layerNames} — no data inside your project area.`,

    // Rebuild last export
    rebuildLastButton: enabled ? 'Rebuild Last Export' : 'Rebuild Last Download',
    rebuildDroppedLayersNotice: (layerNames: string) =>
      enabled
        ? `Left out ${layerNames} — no longer available inside your project boundary.`
        : `Left out ${layerNames} — no longer available inside your project area.`,
    rebuildProjectionUnavailable: (projectionName: string) =>
      `${projectionName} isn't offered for this project, so your current projection was kept.`,
    rebuildTemplateUnavailable: enabled
      ? "That export's extract template is no longer available, so CivilGrid Default was selected."
      : "That download's extract template is no longer available, so CivilGrid Default was selected.",

    // Submit guard
    submitCooldown: (seconds: number) =>
      enabled ? `You can submit another export in ${seconds}s.` : `You can submit another download in ${seconds}s.`,

    // Export history rows
    downloadReadyHover: enabled ? 'Save this export' : 'Download this file',

    // Layer picker
    layerRequiresApproval: enabled
      ? '⚠️ Export requires approval by data partner'
      : '⚠️ Download requires approval by data partner',
    layerRestrictedToAcres: (acres: number) =>
      enabled
        ? `⚠️ Export restricted to ${acres} acres by data partner`
        : `⚠️ Download restricted to ${acres} acres by data partner`,
    layerAssetsUnknown: enabled
      ? "It's still available to attempt to export."
      : "It's still available to attempt to download.",

    // Restricted / manual-request states
    noLayersSelected: enabled
      ? '⚠️ No Layers Selected. Please select layers to proceed with an export request'
      : '⚠️ No Layers Selected. Please select layers to proceed with a download request',
    areaUnsupported: enabled
      ? '⚠️ Exports for projects greater than 2500 acres are not supported at this time. Please contact support.'
      : '⚠️ Downloads for projects greater than 2500 acres are not supported at this time. Please contact support.',
    requestRequiredHeader: enabled ? 'Export Request Required To Proceed' : 'Download Request Required To Proceed',
    requestRequiredBody: enabled
      ? 'Direct export is not available based on your selections and requires partner approval due to the restrictions below. If you wish to proceed with a direct export, please unselect the restricted layers or'
      : 'Direct download is not available based on your selections and requires partner approval due to the restrictions below. If you wish to proceed with a direct download, please unselect the restricted layers or',
    limitsChecking: enabled ? 'Checking export limits…' : 'Checking download limits…',
    limitsUnavailableTitle: enabled ? "Couldn't check export limits" : "Couldn't check download limits",
    limitsUnavailableBody: enabled
      ? 'Direct export is paused until the limits load. Try again, or submit a request for our team to review.'
      : 'Direct download is paused until the limits load. Try again, or submit a request for our team to review.',
    limitsRetry: 'Try again',
    requestDialogTitle: enabled ? 'Export Request' : 'Download Request',
    requestDialogIntro: enabled
      ? 'You are submitting the following export request:'
      : 'You are submitting the following download request:',
    requestSuccessToast: enabled
      ? 'Your export request was submitted successfully. A representative from CivilGrid will contact you via email to fulfill the request.'
      : 'Your download request was submitted successfully. A representative from CivilGrid will contact you via email to fulfill the request.',
    requestErrorToast: enabled
      ? 'There was an error submitting your export request. Please try again. If the error persists, please contact support@civilgrid.com.'
      : 'There was an error submitting your download request. Please try again. If the error persists, please contact support@civilgrid.com.',

    // History tab
    historyEmptyState: enabled ? 'Your Project Has No Export History' : 'Your Project Has No Download History',
    historyEmptyStateButton: enabled ? 'New Export' : 'New Download',
    historyEmptyTitle: enabled
      ? "Data hasn't been exported from CivilGrid yet."
      : "Data hasn't been downloaded from CivilGrid yet.",
    historyEmptyDescription: enabled
      ? 'Export batches will be listed here once they have been run.'
      : 'Download batches will be listed here once they have been run.',
    historyRequestedByColumn: enabled ? 'Exported By' : 'Downloaded By',
    historyBuildingLabel: enabled ? 'Building Export' : 'Building Download',
    historyFailedLabel: enabled ? 'Export Failed' : 'Download Failed',
    historyProcessingLabel: 'Processing',
    historyDelayedTooltip: enabled
      ? "CivilGrid is working on your export, you will be emailed as soon as it's ready."
      : "CivilGrid is working on your download, you will be emailed as soon as it's ready.",
    historyNoDataTooltip: 'No data was found for the selected layers',
    historyLayersPopoverHeader: enabled ? 'Layers Included In Export' : 'Layers Included In Download',

    // Elsewhere in the app
    onboardingStepTitle: enabled ? 'Export' : 'Download',
    onboardingCta: enabled ? 'Export Data' : 'Download Data',
    planUsageLabel: enabled ? 'Exports' : 'Downloads',
    rolePermissionLabel: enabled ? 'Export data' : 'Download data',
    pricingFeature: enabled ? 'Export to CAD and GIS' : 'Download to CAD and GIS',
  } as const;
}

/**
 * Pre-rename wording, resolved once. Components shared with the legacy ESRI form default to this
 * so that form keeps download copy no matter what the flag says.
 */
export const DOWNLOAD_LABELS = getExportLabels(false);

export function useExportLabels(): ExportLabels {
  return getExportLabels(useExportTerminologyEnabled());
}
